import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type DependencyList,
  type ReactNode,
} from 'react';

/**
 * 화면 "방문(visit)" 동안 유지되는 상태. (Android `@ActivityRetainedScoped` / ViewModel 대응)
 *
 * 가려진 화면을 unmount→remount 해도(=같은 visit) 값이 보존되고, 화면이 실제로 close
 * (pop/Activity finish)되면 RetainedStateProvider 와 함께 통째로 GC 된다 → **수동 정리 불필요.**
 * 무거운 트리는 unmount 로 메모리를 회수하되, 가벼운 상태(스크롤/탭/옵션/folded/로깅 dedupe)만
 * Provider 의 store 에 남긴다.
 *
 * jotai atom 과 달리 키 가공/cleanup 없이 토큰 정체성으로 키잉되고 visit 단위로 자동 회수된다.
 * useRetainedState 는 reactive(여러 컴포넌트가 공유, 변경 시 구독자 리렌더), useRetainedRef 는
 * 명령형(리렌더 없음, 스크롤 offset 등).
 *
 * 사용:
 *   const tabState = retainedState<GoodsDetailTab>('INFORMATION');   // 모듈 스코프
 *   const [tab, setTab] = useRetainedState(tabState);
 *
 * ⚠️ 토큰은 **반드시 모듈 스코프**에 선언(컴포넌트 안 useMemo/useRef 금지). 그래야 remount 를
 * 가로질러 정체성이 안정되어 같은 슬롯으로 복구된다.
 */

export type RetainedToken<T> = {
  /** 디버깅용 식별자. 실제 key 는 토큰 객체의 정체성(참조). */
  readonly _id: number;
  readonly initial: T | (() => T);
};

let _seq = 0;
const makeToken = <T,>(initial: T | (() => T)): RetainedToken<T> => ({
  _id: _seq++,
  initial,
});

/** visit 당 1개인 상태(탭/옵션/스크롤/folded 등). 토큰을 모듈 스코프에 선언해 사용. */
export const retainedState = <T,>(initial: T | (() => T)): RetainedToken<T> =>
  makeToken(initial);

/**
 * 인스턴스별 상태(상품카드별 등). 안정 id(데이터 키)로 호출 — 같은 id 면 같은 토큰을 반환.
 * (jotai `atomFamily` 대응. 값은 visit store 에서 close 시 정리되고, 이 캐시엔 토큰만 남는다.)
 */
export const retainedStateFamily = <P, T>(
  initialFor: (param: P) => T,
): ((param: P) => RetainedToken<T>) => {
  const cache = new Map<P, RetainedToken<T>>();
  return (param: P) => {
    let token = cache.get(param);
    if (!token) {
      token = makeToken<T>(() => initialFor(param));
      cache.set(param, token);
    }
    return token;
  };
};

type AnyToken = RetainedToken<unknown>;
type Store = {
  values: Map<AnyToken, unknown>;
  listeners: Map<AnyToken, Set<() => void>>;
};
const createStore = (): Store => ({ values: new Map(), listeners: new Map() });

// Provider 가 없는 곳(주입 케이스 등)에서 호출되면 context 는 null → 컴포넌트 로컬 store 로
// graceful degrade(= 평범한 useState 처럼 동작, visit 간 교차오염 없음).
const RetainedStateContext = createContext<Store | null>(null);

/**
 * visit 경계에 retained store 를 제공. **반드시 화면 wrapper 레벨**(react-navigation
 * `screenLayout`)에 두어, 가려진 화면 content 가 unmount 돼도 살아남고 화면 close 시에만
 * 함께 unmount 되게 한다. 가려진 화면 unmount 게이트가 있으면 그 게이트 **바깥**에 위치.
 */
export const RetainedStateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const storeRef = useRef<Store | null>(null);
  if (storeRef.current === null) storeRef.current = createStore();
  return (
    <RetainedStateContext.Provider value={storeRef.current}>
      {children}
    </RetainedStateContext.Provider>
  );
};

const resolveInitial = <T,>(initial: T | (() => T)): T =>
  typeof initial === 'function' ? (initial as () => T)() : initial;

// 최초 접근 시 initial 을 store 에 1회 적재(memoize) → getSnapshot 이 안정 값 반환.
const ensure = <T,>(store: Store, token: RetainedToken<T>): T => {
  if (!store.values.has(token)) {
    store.values.set(token, resolveInitial(token.initial));
  }
  return store.values.get(token) as T;
};

const subscribeToken = (store: Store, token: AnyToken, cb: () => void): (() => void) => {
  let set = store.listeners.get(token);
  if (!set) {
    set = new Set();
    store.listeners.set(token, set);
  }
  set.add(cb);
  return () => {
    set?.delete(cb);
  };
};

const useStore = (): Store => {
  const ctx = useContext(RetainedStateContext);
  const localRef = useRef<Store | null>(null);
  if (ctx === null && localRef.current === null) localRef.current = createStore();
  return ctx ?? (localRef.current as Store);
};

/**
 * `useState` 처럼 쓰되, 같은 visit 의 unmount/remount 를 가로질러 값이 보존되고,
 * **같은 토큰을 쓰는 여러 컴포넌트가 값을 공유**(변경 시 모두 리렌더)한다.
 */
export const useRetainedState = <T,>(
  token: RetainedToken<T>,
): readonly [T, (next: T | ((prev: T) => T)) => void] => {
  const store = useStore();
  ensure(store, token); // lazy init (1회)

  const subscribe = useCallback(
    (cb: () => void) => subscribeToken(store, token, cb),
    [store, token],
  );
  const getSnapshot = useCallback(() => store.values.get(token) as T, [store, token]);
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const prev = ensure<T>(store, token);
      const resolved =
        typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      store.values.set(token, resolved);
      store.listeners.get(token)?.forEach(listener => listener());
    },
    [store, token],
  );

  return [value, setValue] as const;
};

/**
 * `useRef` 의 retained 버전. `.current` 읽기/쓰기가 visit store 와 직접 연결되어
 * **리렌더를 일으키지 않는다.** 스크롤 오프셋처럼 명령형으로 자주 쓰고(매 스크롤 정지)
 * remount 때 읽어 복구하는 값에 사용. (반응이 필요한 상태는 useRetainedState 를 사용.)
 */
export const useRetainedRef = <T,>(token: RetainedToken<T>): { current: T } => {
  const store = useStore();
  ensure(store, token);

  const handle = useRef<{ current: T } | null>(null);
  if (handle.current === null) {
    handle.current = {
      get current(): T {
        return store.values.get(token) as T;
      },
      set current(value: T) {
        store.values.set(token, value);
      },
    };
  }
  return handle.current;
};

// 모든 useRetainedEffect 가 공유하는, visit 동안 유지되는 "완료된 key" 집합.
// 1회성 done 플래그라 상태처럼 토큰 isolation 이 필요 없어 문자열 key 로 충분하다.
const retainedEffectDoneToken = retainedState<Set<string>>(() => new Set());

/**
 * `useEffect` 의 "visit 당 1회" 버전 — 가려진 화면 unmount→remount 를 가로질러 한 번만 실행.
 * VIEW/노출/광고/어트리뷰션 POST 같은 방문당 1회 부수효과에 사용(재발화 방지).
 *
 * key 는 visit(Provider) 스코프에서만 유일하면 된다(한 화면 안에서 이벤트명이 곧 유일).
 * 모듈 스코프 토큰 선언이 필요 없고, per-instance 면 `like-${sno}` 처럼 동적 key 도 가능.
 *
 * effect 가 `false` 를 반환하면 "아직 안 됨"으로 보고 마킹하지 않아 다음 렌더에 재시도한다
 * (데이터 미도착/게이트 미충족/조건부 발화 실패 등). 그 외(undefined/true)면 완료로 마킹.
 */
export const useRetainedEffect = (
  key: string,
  effect: () => boolean | void,
  deps: DependencyList,
): void => {
  const [done] = useRetainedState(retainedEffectDoneToken);
  const effectRef = useRef(effect);
  effectRef.current = effect;
  useEffect(() => {
    if (done.has(key)) return;
    if (effectRef.current() === false) return; // 미완료 → 마킹 X(재시도)
    done.add(key); // in-place mutate (1회성 플래그라 리렌더 불필요)
    // effect 는 ref 로 최신 참조; deps 는 호출부가 게이팅용으로 전달.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, done, ...deps]);
};
